import React from "react";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useToast } from "../context/ToastContext";
import { FaMapMarkerAlt, FaPhoneAlt, FaEnvelope, FaCheckCircle } from "react-icons/fa";

export default function Contact() {
  const { addToast } = useToast();
  const [loading, setLoading] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const closeBtnRef = React.useRef(null);

  // focus the Close button when modal opens
  React.useEffect(() => {
    if (open) closeBtnRef.current?.focus();
  }, [open]);

  // basic focus trap + ESC to close
  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  const fadeUp = (d = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.2 },
    transition: { duration: 0.5, delay: d },
  });

  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    company: "", // honeypot (leave empty)
    consent: false,
  });

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const validate = () => {
    if (!form.name.trim() || form.name.trim().length < 2) {
      addToast("Please enter your full name.", "warning");
      return false;
    }
    const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email);
    if (!emailOk) {
      addToast("Please enter a valid email.", "warning");
      return false;
    }
    if (form.phone && form.phone.replace(/\D/g, "").length < 10) {
      addToast("Phone should be 10 digits (numbers only).", "warning");
      return false;
    }
    if (!form.message.trim() || form.message.trim().length < 10) {
      addToast("Please include a brief message (10+ characters).", "warning");
      return false;
    }
    if (!form.consent) {
      addToast("Please agree to be contacted.", "warning");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!validate()) return;

    if (form.company) {
      addToast("Submission blocked.", "error");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error("Failed to send");

      addToast("Thanks! We’ll be in touch shortly.", "success");
      setForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        company: "",
        consent: false,
      });
      setOpen(true); // <-- open modal
    } catch (err) {
      addToast("Something went wrong. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const sectionTitle = "text-4xl font-extrabold tracking-tight text-[#002b5c]";
  const sectionSub = "text-gray-600 max-w-3xl mx-auto mt-2 text-base md:text-lg";

  return (
    <div className="overflow-y-auto scrollbar-hide font-sans text-[#002b5c] bg-white">
      <Helmet>
        <title>Contact Us | Your Choice Associates</title>
        <meta
          name="description"
          content="Contact Your Choice Associates for help with Medicare and Life Insurance. Our licensed brokers are here to help."
        />
      </Helmet>

      <Navbar />

      {/* HERO */}
      <section className="relative">
        <img
          src="/images/hero.png"
          alt="Contact us hero"
          className="w-full h-[360px] md:h-[420px] object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#002b5c]/80" />
        <div className="absolute inset-0 flex items-center">
          <div className="w-full px-6">
            <div className="max-w-5xl mx-auto text-center text-white">
              <motion.h1 {...fadeUp(0)} className="text-4xl md:text-5xl font-extrabold leading-tight">
                Get in Touch
              </motion.h1>
              <motion.p {...fadeUp(0.12)} className="mt-3 text-lg md:text-xl text-white/90 max-w-3xl mx-auto">
                Have questions about coverage? Send us a note and a licensed broker will reach out.
              </motion.p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-6xl mx-auto grid md:grid-cols-5 gap-10">
          {/* Left: Contact details */}
          <motion.aside {...fadeUp(0)} className="md:col-span-2">
            <h2 className={sectionTitle}>Contact Details</h2>
            <p className={sectionSub}>
              Prefer to talk? We’re happy to answer questions and help you compare plan options.
            </p>

            <div className="mt-8 space-y-5 text-[#002b5c]">
              <div className="flex items-start gap-3">
                <FaMapMarkerAlt className="mt-1 text-[#e31c3d]" />
                <div>
                  76 Mamaroneck Avenue Suite 22
                  <br />White Plains, NY 10601
                </div>
              </div>
              <div className="flex items-center gap-3">
                <FaPhoneAlt className="text-[#e31c3d]" />
                <a href="tel:+19143584068" className="font-semibold hover:underline">
                  +1 (914) 358-4068
                </a>
              </div>
              <div className="flex items-center gap-3">
                <FaEnvelope className="text-[#e31c3d]" />
                <a href="mailto:support@yourchoiceassociates.org" className="font-semibold hover:underline">
                  support@yourchoiceassociates.org
                </a>
              </div>
            </div>

            <div className="mt-8 rounded-2xl bg-[#002b5c]/5 border border-[#002b5c]/10 p-4 text-sm text-[#002b5c]">
              <div className="font-semibold">Hours</div>
              <div>Mon–Fri: 9:00 AM – 6:00 PM ET</div>
              <div className="text-[#002b5c]/70">Same-day callbacks when possible.</div>
            </div>
          </motion.aside>

          {/* Right: Form */}
          <motion.div {...fadeUp(0.05)} className="md:col-span-3">
            <form
              onSubmit={onSubmit}
              className="bg-white rounded-2xl p-6 md:p-8 border border-[#002b5c]/10 shadow-sm"
              noValidate
            >
              {/* Honeypot */}
              <input
                type="text"
                name="company"
                value={form.company}
                onChange={onChange}
                className="hidden"
                tabIndex={-1}
                autoComplete="off"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="text-sm font-semibold text-[#002b5c]/80">
                    Full Name
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    value={form.name}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-[#002b5c]/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
                    placeholder="Jane Doe"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="email" className="text-sm font-semibold text-[#002b5c]/80">
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-[#002b5c]/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
                    placeholder="you@example.com"
                    required
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="text-sm font-semibold text-[#002b5c]/80">
                    Phone (optional)
                  </label>
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength={20}
                    value={form.phone}
                    onChange={(e) =>
                      onChange({
                        target: {
                          name: "phone",
                          value: e.target.value.replace(/[^\d()+\-.\s]/g, ""),
                        },
                      })
                    }
                    className="mt-1 w-full rounded-xl border border-[#002b5c]/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
                    placeholder="(555) 123-4567"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="text-sm font-semibold text-[#002b5c]/80">
                    Subject (optional)
                  </label>
                  <input
                    id="subject"
                    name="subject"
                    type="text"
                    value={form.subject}
                    onChange={onChange}
                    className="mt-1 w-full rounded-xl border border-[#002b5c]/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
                    placeholder="Question about coverage"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="text-sm font-semibold text-[#002b5c]/80">
                  Message
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  className="mt-1 w-full rounded-2xl border border-[#002b5c]/20 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#002b5c]/30"
                  placeholder="Tell us a little about what you need…"
                  required
                />
              </div>

              <div className="mt-4 flex items-start gap-3">
                <input
                  id="consent"
                  name="consent"
                  type="checkbox"
                  checked={form.consent}
                  onChange={onChange}
                  className="mt-1 h-4 w-4 rounded border-[#002b5c]/30 text-[#002b5c] focus:ring-[#002b5c]"
                />
              <label htmlFor="consent" className="text-sm text-gray-700">
                I agree to receive <strong>SMS, calls, and emails</strong> from <strong>Your Choice Associates</strong> related to my request, follow-ups, and appointment updates. Message frequency varies.
                Msg&data rates may apply. Reply <strong>STOP</strong> to cancel, <strong>HELP</strong> for help.
                <span className="block">
                  Consent is not a condition of purchase. See our{" "}
                  <a href="/privacy" className="underline">Privacy Policy</a> and{" "}
                  <a href="/sms-terms" className="underline">SMS Terms</a>.
                </span>
              </label>

              </div>

              <div className="mt-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center justify-center gap-2 bg-[#002b5c] text-white px-6 py-3 rounded-full font-semibold shadow-md hover:bg-[#001b35] transition disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    "Send Message"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#e31c3d]/5 py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#002b5c]">
            Prefer to speak with someone now?
          </h2>
          <p className="text-gray-700 mt-3">Call us and a licensed broker will help you right away.</p>
          <div className="mt-6">
            <a
              href="tel:+19143584068"
              className="inline-flex items-center justify-center gap-2 bg-[#e31c3d] hover:bg-[#c01730] text-white px-6 py-3 rounded-full font-semibold shadow-md transition"
            >
              <FaPhoneAlt /> +1 (914) 358-4068
            </a>
          </div>
        </div>
      </section>

      <Footer />

      {/* Success Modal */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            aria-modal="true"
            role="dialog"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/50"
              onClick={() => setOpen(false)}
            />
            {/* Panel */}
            <motion.div
              className="relative mx-4 w-full max-w-lg rounded-2xl bg-white shadow-2xl border border-[#002b5c]/10"
              initial={{ y: 24, scale: 0.98, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 24, scale: 0.98, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 22 }}
            >
              <div className="p-6 md:p-8 text-center">
                <FaCheckCircle className="mx-auto text-4xl text-green-500" />
                <h3 className="mt-4 text-2xl font-bold text-[#002b5c]">
                  Thanks! We received your message.
                </h3>
                <p className="mt-2 text-gray-600">
                  A licensed broker will get back to you shortly. If it’s urgent, call us now at{" "}
                  <a href="tel:+19143584068" className="font-semibold text-[#e31c3d] hover:underline">
                    +1 (914) 358-4068
                  </a>.
                </p>

                <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="tel:+19143584068"
                    className="inline-flex items-center justify-center rounded-full px-5 py-3 bg-[#002b5c] text-white font-semibold hover:bg-[#001b35]"
                  >
                    Call Now
                  </a>
                  <button
                    ref={closeBtnRef}
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center rounded-full px-5 py-3 border border-[#002b5c]/20 font-semibold text-[#002b5c] hover:bg-[#002b5c]/5"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
